import { CommonModule } from '@angular/common';
import { Component, DestroyRef, effect, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { NgHttpCachingService } from 'ng-http-caching';
import { catchError, combineLatest, map, of } from 'rxjs';

import {
	DeviceClaimingApiService,
	DeviceDiscoveryApiService,
	ScheduledJobsApiService
} from '@shure/cloud/device-management/devices/data-access';
import { OktaInterfaceService, monitorLoginState } from '@shure/cloud/shared/okta/data-access';
import { BreakpointService } from '@shure/cloud/shared/services/media-breakpoints';
import { SecondaryNavService } from '@shure/cloud/shared/services/secondary-nav-service';
import {
	SwitchOrganizationModule,
	SwitchOrganizationService
} from '@shure/cloud/shared/switch-organization/feature-switch-organization';
import {
	FooterModule,
	HeaderToolbarModule,
	SecondaryNavList,
	ShSecondaryNavComponent,
	SideNavComponent
} from '@shure/cloud/shared/ui/components';
import { AnalyticsEvents, AnalyticsService } from '@shure/cloud/shared/utils/analytics';
import { APP_ENVIRONMENT, AppEnvironment } from '@shure/cloud/shared/utils/config';
import { FEATURE_FLAG_SERVICE, CloudFeatureFlagsService } from '@shure/cloud/shared/utils/feature-flags';
import { PrismLayoutComponent, PrismNavComponent, PrismSnackbarService } from '@shure/prism-angular-components';
import { ILogger } from '@shure/shared/angular/utils/logging';

@Component({
	selector: 'sh-app-shell',
	imports: [
		CommonModule,
		FooterModule,
		HeaderToolbarModule,
		PrismLayoutComponent,
		PrismNavComponent,
		RouterModule,
		ShSecondaryNavComponent,
		SideNavComponent,
		SwitchOrganizationModule,
		TranslocoModule
	],
	templateUrl: './app-shell.component.html',
	styleUrl: './app-shell.component.scss'
})
export class AppShellComponent implements OnInit {
	public readonly isSmallDevice = this.breakpointService.isLteSmall;
	public readonly showNotifications: boolean;
	protected readonly secondaryNavListItems = this.secondaryNavService.secondaryNavListItems;
	public readonly showScheduledJobsNavItem = toSignal(this.featureFlagService.getFeatureFlag$('cdm-scheduled-jobs'));
	private secondaryNavItems: SecondaryNavList[] = [
		{
			id: 'devices',
			key: 'devices',
			feature: '',
			path: 'devices',
			listItem: 'cloud.device-management.navitems.devices',
			icon: 'sh-network',
			keyInParams: false
		},
		{
			id: 'pending-devices',
			key: 'pending-devices',
			feature: '',
			path: 'pending-devices',
			listItem: 'cloud.device-management.navitems.pending-devices',
			icon: 'help',
			keyInParams: false
		}
	];
	private scheduledJobNavItem: SecondaryNavList = {
		id: 'scheduled',
		key: 'scheduled',
		feature: '',
		path: 'scheduled',
		listItem: 'cloud.device-management.navitems.scheduled',
		icon: 'access_time',
		keyInParams: false
	};

	private deviceCount = toSignal(this.discoveryService.discoveredDevicesCountAfterDiscovery$());
	private pendingClaimCount = toSignal(
		this.deviceClaimService.getPendingClaims$().pipe(map((pendingClaims) => pendingClaims.length))
	);
	private scheduledJobCount = toSignal(this.scheduledJobsService.getScheduledJobsCount$());

	constructor(
		private readonly router: Router,
		private readonly activatedRoute: ActivatedRoute,
		private readonly orgSwitcherService: SwitchOrganizationService,
		@Inject(PrismSnackbarService) private readonly snackbarService: PrismSnackbarService,
		private readonly translocoService: TranslocoService,
		public readonly oktaIntf: OktaInterfaceService,
		public readonly breakpointService: BreakpointService,
		private readonly destroyRef: DestroyRef,
		private readonly secondaryNavService: SecondaryNavService,
		@Inject(APP_ENVIRONMENT) private appEnv: AppEnvironment,
		private ngHttpCachingService: NgHttpCachingService,
		private readonly logger: ILogger,
		private readonly discoveryService: DeviceDiscoveryApiService,
		private readonly deviceClaimService: DeviceClaimingApiService,
		private readonly analyticsService: AnalyticsService,
		@Inject(FEATURE_FLAG_SERVICE) private readonly featureFlagService: CloudFeatureFlagsService,
		private readonly scheduledJobsService: ScheduledJobsApiService
	) {
		this.logger = logger.createScopedLogger('CDM AppShell');
		this.showNotifications = Boolean(this.appEnv.cdmFeatureFlags?.showNotificationsInHeader);

		// Setup secondary nav
		this.secondaryNavService.setSecondaryNavListItems(this.secondaryNavItems);

		effect(() => {
			if (this.showScheduledJobsNavItem()) {
				this.secondaryNavService.addSecondaryNavListItem(this.scheduledJobNavItem);
			}
		});

		effect(() => {
			this.updateSecondaryNavItem('devices', this.deviceCount());
		});

		effect(() => {
			this.updateSecondaryNavItem('scheduled', this.scheduledJobCount());
		});

		effect(() => {
			this.updateSecondaryNavItem('pending-devices', this.pendingClaimCount());
		});

		monitorLoginState(oktaIntf, {
			onLogIn: this.onLogIn.bind(this),
			onLogOut: this.onLogOut.bind(this)
		});
	}

	public ngOnInit(): void {
		this.logger.debug('CDM', 'ngOnInit');
	}

	public onLogIn(): void {
		this.ngHttpCachingService.clearCache();
		if (!this.appEnv.cdmFeatureFlags?.showOrgSwitcher) {
			return;
		}
		const requestedOrgId$ = this.activatedRoute.queryParamMap.pipe(
			map((queryParamMap) => queryParamMap.get('orgId')),
			// remove possible leading/training quotes
			map((orgId) => orgId?.replace(/["']/g, '') ?? ''),
			takeUntilDestroyed(this.destroyRef)
		);
		const currentOrgId$ = this.oktaIntf.getUserProfile$().pipe(
			map((userProfile) => userProfile.orgId),
			takeUntilDestroyed(this.destroyRef)
		);
		const allOrgIds$ = this.orgSwitcherService.getAllMyOrganizations().pipe(
			map((orgData) => {
				const items = orgData.body.body?.items;
				if (!items) {
					return [];
				}
				return items.map((item) => item.id ?? '');
			}),
			catchError(() => of([])),
			takeUntilDestroyed(this.destroyRef)
		);

		combineLatest([requestedOrgId$, currentOrgId$, allOrgIds$])
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: ([requestedOrgId, currentOrgId, allOrgIds]) => {
					this.checkAndSwitchOrgs(requestedOrgId, currentOrgId, allOrgIds);
				}
			});
	}
	public onLogOut(): void {
		this.logger.debug('CDM', 'onLogOut');
	}

	public sendSignOutAnalyticsEvent(): void {
		// removing sign in state so that on next login we can track sign in again
		localStorage.removeItem(AnalyticsEvents.SignIn);
		this.analyticsService.analyticDataCollector(AnalyticsEvents.SignOut, {});
	}

	private checkAndSwitchOrgs(requestedOrgId: string, currentOrgId: string, allOrgIds: string[]): void {
		// no requested org, can't switch
		if (!requestedOrgId || requestedOrgId.length === 0) {
			return;
		}

		// already on this org... no reason to switch
		if (requestedOrgId === currentOrgId) {
			// remove orgId query parameter
			this.router.navigate(['/']);
			return;
		}

		// if the requested Org Id is not a valid org for the user put up a snackbar,
		// then remove the orgId parameter from the URL by routing to '/' w/out the query parameter
		if (!allOrgIds.includes(requestedOrgId)) {
			this.snackbarService.open({
				message: this.translocoService.translate('cloud.shared.switch-org.not-a-member-of-org'),
				actionText: this.translocoService.translate('common.snackbar.dismiss')
			});
			this.router.navigate(['/']);
			return;
		}

		// switch to requested Org
		this.logger.debug('Switching orgs', 'New value %o', requestedOrgId);
		this.oktaIntf
			.setOktaSessionTenant$(requestedOrgId, false)
			.pipe(
				map(() => true),
				catchError(() => {
					return of(false);
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((result: boolean) => {
				if (result) {
					// it was decided to not keep the orgId in the URL.
					// route to / to remove it, then reload the page.
					this.router.navigate(['/']).then(() => {
						window.location.reload();
					});
				}
			});
	}

	private updateSecondaryNavItem(name: string, count: number | undefined): void {
		this.secondaryNavService.updateSecondaryNavListItem(name, {
			listItemPostfix: count !== undefined && count > 0 ? `(${count})` : undefined
		});
	}
}
